import { useQuery } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { useViewMode } from '@/contexts/ViewModeContext';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook to check if the current user has manager-level access.
 * Uses React Query for caching - result persists across navigation.
 * 
 * Behavior depends on view mode:
 * - Admin View: Returns true if user has manager/admin/staff role in agency OR is system admin
 * - Consultant View: Returns true ONLY if user has manager/admin/staff role in agency_members
 *   (System admin role is ignored - capabilities depend on agency role only)
 */
export function useManagerRole() {
  const { user } = useAuth();
  const { isConsultantView } = useViewMode();

  const { data: isManager = false, isLoading } = useQuery({
    queryKey: ['manager-role', user?.id, isConsultantView],
    queryFn: async () => {
      if (!user?.id) return false;

      // Always check agency_members for manager/admin/staff role
      const { data: agencyMember } = await supabase
        .from('agency_members')
        .select('role')
        .eq('user_id', user.id)
        .in('role', ['manager', 'admin', 'staff'])
        .maybeSingle();

      if (agencyMember) return true;

      // In Consultant View, only agency role matters - skip system role check
      if (isConsultantView) {
        return false;
      }

      // In Admin View, also check if user is a system admin (profiles.role)
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('user_id', user.id)
        .maybeSingle();

      return profile?.role === 'admin' || profile?.role === 'staff';
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes - manager status rarely changes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  return { isManager, loading: isLoading, user };
}
